import { makeStyles } from "tss-react/mui"
import { Grid, Stack, Typography } from "@mui/material"

import { DateTime } from "luxon"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { Box } from "@mui/system"
import {
    fetchMeetingServiceOrders,
    fetchMeetingServiceOrdersForExport,
    MeetingServiceOrdersForExportResponse,
    MeetingServiceOrdersResponse,
    MerchantMeetingServiceOrdersQuery,
} from "../meetingServiceApi"
import { useMemo, useState } from "react"

import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { SharePrintButtons } from "../../../../../nonAdminUser/reports/MeetingsReceipt/components/SharePrintButtons"
import GeneralErrorAnim from "../../../../../../shared/assets/general-error-lottie-anim.json"
import { ModalHeader } from "./deliveryListModal/ModalHeader"
import {
    DeliveryFilter,
    DeliveryFilterValue,
    DELIVERY_FILTER_ALL,
    GroupBy,
    MeetingServiceOrderStatus,
    getDeliveryTypeKeyByValue,
    getDeliveryFilterAsDeliveryType,
} from "../meetingServiceTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { ErrorView } from "../../../../../../shared/components/errors/ErrorView"
import { groupOrdersByDateAndType } from "./deliveryListModal/helpers"
import { DeliveryGroups } from "./deliveryListModal/DeliveryGroups"
import { DeliveryListHeader } from "./deliveryListModal/DeliveryListHeader"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    container: {
        backgroundColor: tailwindPalette.slate[100],
        minHeight: "100vh",
        gap: "3.5rem",
        paddingBottom: "3.5rem",
        justifyContent: "start",
        [theme.breakpoints.down("sm")]: {
            backgroundColor: theme.palette.common.white,
            gap: 0,
        },
        "@media print": {
            backgroundColor: "transparent",
            minHeight: "auto",
            width: "100vw",
            paddingBottom: 0,
        },
    },
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
    },
    slide: {
        width: "80%",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px 0px #00000026;",
        paddingBottom: "3.5rem",
        "@media print": {
            width: "100%",
            display: "block",
            boxShadow: "none",
            backgroundColor: "white",
        },
    },
    header: {
        padding: "1.25rem 1.5rem",
        position: "sticky",
        top: 0,
        maxHeight: "4.5rem",
        backgroundColor: "white",
        boxShadow: "0px 2px 6px 0px #00000014",
        "@media print": {
            display: "none",
        },
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
            padding: "0 1.5rem",
            zIndex: 1,
            maxHeight: "auto",
            boxShadow: "0px 3px 1px -2px #00000033,  0px 2px 2px 0px #00000024, 0px 1px 5px 0px #0000001F",
        },
    },
    actionButtons: {
        justifySelf: "end",
        flex: "none",
    },
    emptyState: {
        textAlign: "center",
        padding: "4rem 2rem",
    },
}))

export type DeliveryListMode = "selected" | "all"
export type DeliveryListDateState = {
    startDate: DateTime | null
    endDate: DateTime | null
}
export type MeetingServiceOrdersDependencies = Parameters<typeof fetchMeetingServiceOrders>[0]

type StartPageLayoutProps = Readonly<{
    open: boolean
    onClose: () => void
    mode: DeliveryListMode
    orderIds?: number[]
    meetingServiceDependencies: MeetingServiceOrdersDependencies
    tab?: MeetingServiceOrderStatus
}>

const transformOrdersForExport = (data: MeetingServiceOrdersForExportResponse) => data?.orders
const selectMeetingService = (data: MeetingServiceOrdersResponse) => data?.orders || []

export function OrderDeliveryList({
    mode,
    orderIds,
    meetingServiceDependencies,
    tab,
    open,
    onClose,
}: StartPageLayoutProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { startDeliveryDate, endDeliveryDate, startBookedDate, endBookedDate, period, ...rest } =
        meetingServiceDependencies
    const initStartDate = startDeliveryDate || startBookedDate
    const initEndDate = endDeliveryDate || endBookedDate
    const userId = useAppSelector(selectEffectiveUserId) || 0
    const [groupBy, setGroupBy] = useState<GroupBy>("location")
    const [deliveryTypeState, setDeliveryTypeState] = useState<DeliveryFilterValue>(DELIVERY_FILTER_ALL)

    const isAuthenticated = Boolean(userId)
    const isSelectMode = mode === "selected"

    const dateTimeFactory = useDateTime()
    const [dateState, setDateState] = useState<DeliveryListDateState>({
        startDate: initStartDate ? dateTimeFactory.fromISO(initStartDate) : null,
        endDate: initEndDate ? dateTimeFactory.fromISO(initEndDate) : null,
    })

    const {
        data: exportOrders = [],
        isFetching: isFetchingOrdersForExport,
        error: exportOrdersError,
    } = useApiQuery(fetchMeetingServiceOrdersForExport, {
        queryName: MerchantMeetingServiceOrdersQuery.ORDERS_FOR_EXPORT,
        dependencies: {
            orderIds,
        },
        enabled: isAuthenticated && isSelectMode && Boolean(orderIds?.length),
        select: transformOrdersForExport,
    })

    const excludeDateTime = [MeetingServiceOrderStatus.NOT_APPROVED, MeetingServiceOrderStatus.TO_BE_BOOKED].some(
        (t) => t === tab,
    )

    const {
        data: allOrders = [],
        isFetching: isFetchingAllOrders,
        error: allOrdersError,
    } = useApiQuery(fetchMeetingServiceOrders, {
        queryName: MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
        dependencies: {
            ...rest,
            ...(deliveryTypeState !== DELIVERY_FILTER_ALL
                ? { deliveryType: getDeliveryTypeKeyByValue(getDeliveryFilterAsDeliveryType(deliveryTypeState)!) }
                : {}),
            startDeliveryDate: !excludeDateTime ? dateState.startDate?.toISODate() ?? undefined : undefined,
            endDeliveryDate: !excludeDateTime ? dateState.endDate?.toISODate() ?? undefined : undefined,
            startBookedDate: !excludeDateTime ? dateState.startDate?.toISODate() ?? undefined : undefined,
            endBookedDate: !excludeDateTime ? dateState.endDate?.toISODate() ?? undefined : undefined,
        },
        enabled: isAuthenticated && !isSelectMode,
        select: selectMeetingService,
    })

    const orders = isSelectMode ? exportOrders : allOrders
    const isFetching = isSelectMode ? isFetchingOrdersForExport : isFetchingAllOrders
    const loadError = isSelectMode ? exportOrdersError : allOrdersError

    const grouped = useMemo(
        () => groupOrdersByDateAndType(orders, groupBy, dateTimeFactory),
        [orders, groupBy, dateTimeFactory],
    )

    const hasNoOrders = !isFetching && !loadError && orders.length === 0

    const content = loadError ? (
        <ErrorView
            lottieAnimation={GeneralErrorAnim}
            title="A problem occurred"
            message="Failed to load"
            buttonLabel="Close"
            onClose={onClose}
        />
    ) : (
        <Stack direction="column" className={classes.container}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" className={classes.header}>
                <ModalHeader
                    dateState={dateState}
                    setDateState={setDateState}
                    isSelectMode={isSelectMode}
                    deliveryType={deliveryTypeState}
                    setDeliveryType={setDeliveryTypeState}
                    groupBy={groupBy}
                    setGroupBy={setGroupBy}
                    ordersLength={orders.length}
                />

                <Box className={classes.actionButtons}>
                    <SharePrintButtons onClose={onClose} hasShare={false} hasClose={true} />
                </Box>
            </Stack>
            {isFetching ? (
                <Grid container direction="column" component="main" alignItems="center" justifyContent="center">
                    <LoadingSpinner />
                </Grid>
            ) : (
                <Box id="printable-content" className={classes.slideContainer}>
                    <Stack direction="column" spacing="2rem" className={classes.slide}>
                        <DeliveryListHeader
                            dateState={dateState}
                            label={l10n.getString("meeting-service-delivery-list")}
                        />

                        {hasNoOrders ? (
                            <Box className={classes.emptyState}>
                                <LocalizedStrict id="meeting-service-delivery-list-empty">
                                    <Typography variant="h6">No delivery orders for the selected criteria</Typography>
                                </LocalizedStrict>
                            </Box>
                        ) : (
                            <DeliveryGroups groupBy={groupBy} grouped={grouped} dateTimeFactory={dateTimeFactory} />
                        )}
                    </Stack>
                </Box>
            )}
        </Stack>
    )

    return (
        <StandardDialog open={open} onClose={onClose} isFullScreen hideCloseIcon topbar={<></>}>
            {content}
        </StandardDialog>
    )
}
